import React from "react";
import ManageLocationsDisplay from "./ManageLocationsDisplay";
import IntegrationsDisplay from "./IntegrationsDisplay";
import HoursOfOperationDisplay from "./HoursOfOperationDisplay";
import LaborLawsDisplay from "./LaborLawsDisplay";
import StoreGroupsDisplay from "./StoreGroupsDisplay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CableIcon from "@mui/icons-material/Cable";
import GavelIcon from "@mui/icons-material/Gavel";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_hours_for_all_stores,
  API_update_hours_for_all_stores,
  API_get_manager_stores_rightwork_data,
  API_update_manager_store_rightwork_data,
  API_get_manager_stores_integration_data,
  API_post_manager_store_integration_data,
  API_list_pos_stores,
  API_refresh_pos_locations,
  API_create_store_from_pos_store,
  API_archive_pos_store,
  API_get_company_feature_settings,
  API_bulk_update_feature_settings,
  API_bulk_apply_config_defaults,
  API_get_store_groups,
} from "../../../constants";
import { formatDate } from "../requests/utilities/helpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SettingsPageLoading } from "../../../components/shared/SettingsPageLayout";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import CompanyNameSidebar from "../home/top_bar_components/CompanyNameSidebar";
import useLocationsIntegrationsHoursStore from "../../../stores/locationsIntegrationsHoursStore";
import { useOnboardingIntent } from "../onboarding/onboardingNavigation";

const ONBOARDING_INTENT_DISPLAY_MAP = {
  // Section only, no modal: the Setup Assistant opens it behind the chat.
  integrations: "Integrations",
  connect_pos: "Integrations",
  import_stores: "Integrations",
  manage_locations: "Locations",
  hours_of_operation: "Hours",
  labor_laws: "Labor Laws",
};

function LocationsIntegrationsHoursSettingsDisplay(props) {
  const [data, setData] = React.useState([]);
  const { display: storedDisplay, setDisplay } = useLocationsIntegrationsHoursStore();
  const display = props.settingsView || storedDisplay;
  const [integrationsVisited, setIntegrationsVisited] = React.useState(display === "Integrations");
  React.useEffect(() => {
    if (display === "Integrations") setIntegrationsVisited(true);
  }, [display]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSavingData, setIsSavingData] = React.useState(false);
  const [isRefreshingPosLocations, setIsRefreshingPosLocations] = React.useState(false);
  const [integrationIntent, setIntegrationIntent] = React.useState(null);
  // The location the Setup Assistant is walking through: Hours and Labor
  // Laws scroll to it. A fresh object per intent so the same store can be
  // focused twice in a row.
  const [focusStore, setFocusStore] = React.useState(null);
  const [focusEditor, setFocusEditor] = React.useState(null);
  const NAVBAR_FEATURE_FLAGS = [
    "scheduling_enabled",
    "overview_enabled",
    "reporting_enabled",
    "ai_reporting_enabled",
    "dashboard_enabled",
    "live_labor_enabled",
    "guest_reviews_enabled",
    "headcount_planning_enabled",
    "daily_plan_enabled",
    "boards_enabled",
    "approvals_enabled",
    "published_schedule_enabled",
    "chat_enabled",
    "employees_enabled",
    "logbook_enabled",
    "maintenance_enabled",
    "it_tickets_enabled",
    "marketing_tickets_enabled",
    "menu_tickets_enabled",
    "payroll_enabled",
    "training_enabled",
    "engagement_enabled",
    "documents_enabled",
    "assistant_enabled",
    "operations_assistant_enabled",
    "deployment_enabled",
    "contests_enabled",
    "check_ins_enabled",
  ];

  const companyOptions = React.useMemo(() => {
    const unique = [];
    props.storeNames?.forEach((s) => {
      if (!unique.some((c) => c.id === s.company)) {
        unique.push({
          id: s.company,
          name: s.company_info?.company_name || s.company_info?.name,
          ...s.company_info,
        });
      }
    });
    return unique;
  }, [props.storeNames]);

  const [selectedCompanyId, setSelectedCompanyId] = React.useState(
    props.settingsCompanyId || props.company?.id
  );

  React.useEffect(() => {
    if (!selectedCompanyId && companyOptions.length > 0) {
      setSelectedCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, selectedCompanyId]);

  const selectedCompany =
    companyOptions.find((c) => c.id === selectedCompanyId) || {};

  // Companies where the user holds a company_admin / rightwork_internal STM.
  // get_company_feature_settings is guarded by UserIsCompanyAdmin, so it must
  // only be requested for these — otherwise selecting a company where the user
  // has lower access 403s and (via Promise.all) blocks the whole page load.
  const adminCompanyIds = React.useMemo(() => {
    const ADMIN_PERMISSIONS = new Set(["company_admin", "rightwork_internal"]);
    const adminStoreIds = new Set(
      (props.storeOptions || [])
        .filter((m) => ADMIN_PERMISSIONS.has(m?.permission_type))
        .map((m) => m.store)
    );
    const companyIds = new Set();
    (props.storeNames || []).forEach((s) => {
      if (s?.company != null && adminStoreIds.has(s.id)) {
        companyIds.add(s.company);
      }
    });
    return companyIds;
  }, [props.storeOptions, props.storeNames]);

  const isAdminForSelectedCompany =
    selectedCompanyId != null && adminCompanyIds.has(selectedCompanyId);

  const default_api_calls = {
    hoursOfOperation: API_get_hours_for_all_stores,
    locationsData: API_get_manager_stores_rightwork_data,
    integrationsData: API_get_manager_stores_integration_data,
    posStores: API_list_pos_stores,
    // Only fetch the admin-only company defaults where the user is an admin.
    ...(isAdminForSelectedCompany
      ? { companyFeatureSettings: API_get_company_feature_settings }
      : {}),
  };

  // Get the current date
  let currentDate = new Date();

  // // Get the start date of the previous month
  // let lowerBoundDate = new Date(
  //   currentDate.getFullYear(),
  //   currentDate.getMonth() - 1,
  //   1
  // );
  // // Get the end date of the next month
  // let upperBoundDate = new Date(
  //   currentDate.getFullYear(),
  //   currentDate.getMonth() + 2,
  //   0
  // );

  // let lowerBoundDateStr = formatDate(lowerBoundDate);
  // let upperBoundDateStr = formatDate(upperBoundDate);

  let api_params = {
    date_start: formatDate(currentDate),
    company_id: selectedCompanyId,
  };

  const fetch_data = async (api_calls, api_params, showLoading) => {
    setIsLoading(showLoading);
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      // console.log("CALLING APIS");
      const res = await Promise.all(
        api_list.map((api) => getRightworkAPI(api, api_params))
      );
      // Merge only the keys fetched here. A snapshot of `data` from render
      // time would erase whatever another request (store groups) stored
      // while this one was in flight.
      const fetched = {};
      res.forEach((r, i) => { fetched[api_set_data_name[i]] = r.data; });
      setData((prev) => ({ ...prev, ...fetched }));
      setIsLoading(false);
      setIsSavingData(false);
      // Callers read back only the keys they asked for.
      return fetched;
    } catch (error) {
      // Views that load their own data (store groups, tip pools) must not be
      // left on the shared spinner; the shared views keep their own handling.
      if (display === "Store Groups") setIsLoading(false);
      return null;
    }
  };

  const refresh_after_pos_integration = async () => {
    const refreshedData = await fetch_data(
      {
        integrationsData: API_get_manager_stores_integration_data,
        posStores: API_list_pos_stores,
        locationsData: API_get_manager_stores_rightwork_data,
      },
      api_params,
      false
    );

    if (props.refreshMemberStores) {
      await props.refreshMemberStores();
    }

    return refreshedData;
  };

  const handle_pos_integration_complete = async () => {
    const refreshedData = await refresh_after_pos_integration();
    const refreshedPosStores =
      refreshedData?.posStores?.redux?.payload?.pos_stores || [];

    if (selectedCompanyId && refreshedData && refreshedPosStores.length === 0) {
      try {
        await postRightworkAPI(API_refresh_pos_locations, {
          company_id: selectedCompanyId,
        });
      } catch (error) {
        console.error(
          "Error refreshing POS locations after integration completion:",
          error
        );
      }

      await refresh_after_pos_integration();
    }
  };

  const refresh_pos_locations = async () => {
    if (!selectedCompanyId || isRefreshingPosLocations) {
      return;
    }

    setIsRefreshingPosLocations(true);

    try {
      toast.loading("Refreshing company locations...", { autoClose: false });

      const res = await postRightworkAPI(API_refresh_pos_locations, {
        company_id: selectedCompanyId,
      });

      await refresh_after_pos_integration();

      toast.dismiss();
      const providerLabel =
        res?.data?.redux?.payload?.provider_label ||
        res?.data?.provider_label ||
        "POS";
      toast.success(`${providerLabel} company info refreshed.`, {
        autoClose: 3000,
      });
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Unable to refresh company locations.", { autoClose: 3000 });
      }

      console.error("Error:", error);
    } finally {
      setIsRefreshingPosLocations(false);
    }
  };

  // Store groups load on their own request, outside the Promise.all above: a
  // backend that predates the route (a predecessor task mid-rollout) must not
  // reject the whole page load. A failure is recorded so the view can offer
  // a retry instead of spinning.
  const selectedCompanyRef = React.useRef(selectedCompanyId);
  selectedCompanyRef.current = selectedCompanyId;
  const fetch_store_groups = async () => {
    if (!isAdminForSelectedCompany) return;
    // Discard a response for a company the user has since switched away
    // from: a slow answer must not overwrite the current company's groups.
    const requested = selectedCompanyId;
    let next;
    try {
      const res = await getRightworkAPI(API_get_store_groups, api_params);
      next = res.data;
    } catch (error) {
      next = { error: true, company_id: requested };
    }
    if (selectedCompanyRef.current !== requested) return;
    setData((prev) => ({ ...prev, storeGroups: next }));
  };
  React.useEffect(() => {
    if (selectedCompanyId) {
      fetch_data(default_api_calls, api_params, true);
      fetch_store_groups();
    }
  }, [selectedCompanyId]);

  // Setup Assistant changed data through chat without a manual refresh:
  // "pos" = a POS was connected (refetch integration data and pull POS
  // locations if none are imported yet); "locations" = a store was imported
  // or linked to a POS location (refetch stores, hours and POS locations).
  // "hours" = hours of operation changed; "labor_laws" = a store's
  // jurisdiction changed (both refetch the same store data).
  // Seed with the signal present at mount so a stale signal from an earlier
  // assistant action does not refire (and re-POST refresh_pos_locations) on
  // plain navigation.
  const opsRefreshTimestamp = props.opsRefreshSignal?.timestamp;
  const lastOpsRefreshRef = React.useRef(opsRefreshTimestamp);
  React.useEffect(() => {
    if (!opsRefreshTimestamp || opsRefreshTimestamp === lastOpsRefreshRef.current) {
      return;
    }
    lastOpsRefreshRef.current = opsRefreshTimestamp;
    if (!selectedCompanyId || !props.opsRefreshSignal) return;
    const types = props.opsRefreshSignal.types || [];
    if (types.includes("pos")) {
      handle_pos_integration_complete();
    } else if (
      types.includes("locations") ||
      types.includes("hours") ||
      types.includes("labor_laws")
    ) {
      fetch_data(default_api_calls, api_params, false);
    }
  }, [opsRefreshTimestamp]);

  useOnboardingIntent("locationsIntegrationsHoursSettings", (intent) => {
    const nextDisplay = ONBOARDING_INTENT_DISPLAY_MAP[intent.intent] || "Locations";
    setDisplay(nextDisplay);
    setIntegrationIntent(nextDisplay === "Integrations" ? (intent.intent || null) : null);
    const focusStoreId = Number(intent.store_id);
    const at = Date.now();
    setFocusStore(focusStoreId > 0 ? { storeId: focusStoreId, at } : null);
    if (props.settingsOverview) {
      props.onSettingsAreaChange?.(nextDisplay === "Integrations" ? "integrations" : "locations");
      if (["Hours", "Labor Laws"].includes(nextDisplay)) setFocusEditor({
        storeId: focusStoreId > 0 ? focusStoreId : props.homeStore,
        editorId: nextDisplay === "Hours" ? "hours" : "labor-laws",
        bucketKey: nextDisplay === "Hours" ? "location_details" : "labor_compliance", at,
      });
    }
  });

  // ----------- POST APIs ----------- //

  const update_hours_for_all_stores = async (update_hours_payload) => {
    // Prepare data to send
    const requestData = {
      update_hours_payload: JSON.stringify([update_hours_payload]),
    };

    try {
      setIsSavingData(true);
      toast.loading("Updating Hours of Operation...", { autoClose: false });

      const res = await postRightworkAPI(
        API_update_hours_for_all_stores,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Hours of Operation Updated!", { autoClose: 3000 });

        fetch_data(
          {
            hoursOfOperation: API_get_hours_for_all_stores,
          },
          api_params
        );

        return true;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      return false;
    } finally {
      setIsSavingData(false);
    }
  };

  const update_store_info = async (store_data, { contactChanges = {} } = {}) => {
    // Prepare data to send
    const requestData = {
      store_id: store_data.id,
      store_name: store_data.store_name,
      latitude: store_data.latitude,
      longitude: store_data.longitude,
      onboarding_status: store_data.onboarding_status,
    };

    const contactFields = ["public_phone_number", "public_location_address"].filter((field) =>
      Object.prototype.hasOwnProperty.call(contactChanges, field));
    contactFields.forEach((field) => { requestData[field] = contactChanges[field]?.trim() || null; });

    Object.keys(store_data).forEach((key) => {
      if (key.endsWith("_enabled")) {
        requestData[key] = store_data[key];
      }
    });

    if (!store_data.id && selectedCompanyId) {
      requestData.company_id = selectedCompanyId;
    }

    // The district select on the edit drawer. Sent only when the location
    // carries the key, so the backend never clears it on other saves.
    if (store_data.id && "store_group" in store_data) {
      requestData.store_group_uuid = store_data.store_group ?? null;
    }

    try {
      toast.loading("Updating Store Info...", { autoClose: false });

      const res = await postRightworkAPI(
        API_update_manager_store_rightwork_data,
        requestData
      );

      if (res.status === 200) {
        const needsFullRefresh = !store_data.id;
        const dataToRefresh = {
          locationsData: API_get_manager_stores_rightwork_data,
        };

        if (needsFullRefresh) {
          dataToRefresh.hoursOfOperation = API_get_hours_for_all_stores;
          dataToRefresh.integrationsData = API_get_manager_stores_integration_data;
        }

        const refreshed = await fetch_data(dataToRefresh, api_params, false);

        // Always refresh member stores so flag changes propagate to the
        // in-memory state immediately — no page reload required to see new
        // navigation tabs or per-store feature gates take effect.
        await props.refreshMemberStores?.();

        // A backend that predates store groups answers 200 without reading
        // store_group_uuid (a browser can hit it mid-rollout), so judge the
        // group by what came back rather than by the status code: the
        // refreshed row must exist, carry the field, and match. Success is
        // only announced once that holds.
        if ("store_group_uuid" in requestData) {
          // A district change on the drawer must reach the Store groups view too.
          await fetch_store_groups();
          const saved = refreshed?.locationsData?.redux?.payload?.stores?.find((s) => s.id === store_data.id);
          const verified =
            saved && Object.prototype.hasOwnProperty.call(saved, "store_group")
            && (saved.store_group ?? null) === (requestData.store_group_uuid ?? null);
          if (!verified) {
            toast.dismiss();
            toast.error("The location's store group did not save. Try again in a minute.", { autoClose: 5000 });
            return false;
          }
        }

        toast.dismiss();
        if (contactFields.length > 0) {
          const saved = refreshed?.locationsData?.redux?.payload?.stores?.find((s) => s.id === store_data.id);
          if (!saved || contactFields.some((field) =>
            !Object.prototype.hasOwnProperty.call(saved, field) || (saved[field] || null) !== requestData[field]
          )) {
            toast.error("The location's contact details could not be verified. Please try again.", { autoClose: 5000 });
            return false;
          }
        }
        toast.success("Store Info Updated!", { autoClose: 3000 });
        return true;
      }

      return false;
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed

      return false;
    }
  };

  // Applies a set of feature flags at the company level and/or across stores
  // via the shared bulk endpoint. Powers the "Company Default" card:
  //   scope "company_only" -> writes only the company default (new locations
  //     inherit it; existing locations are untouched — not a live cascade).
  //   scope "all"/"stores"  -> pushes the flags to existing locations, which
  //     changes nav gating, so refresh member stores + the locations list.
  const bulk_update_features = async ({
    flags,
    scope,
    store_ids,
    update_company_default,
  }) => {
    if (!selectedCompanyId || !flags || Object.keys(flags).length === 0) {
      return false;
    }

    // flags is sent as a plain object — postRightworkAPI posts a JSON body and
    // the endpoint requires flags to be a dict (a stringified value 400s).
    const basePayload = {
      company_id: selectedCompanyId,
      flags,
      scope,
    };
    if (update_company_default !== undefined) {
      basePayload.update_company_default = update_company_default;
    }

    // scope "stores" is capped per request by the backend (MAX_BULK_STORE_IDS),
    // so chunk the ids — applying a company default to a large displayed set
    // must still cover every one. Other scopes are a single request.
    const BULK_STORE_ID_CHUNK = 100;
    let payloads;
    if (scope === "stores") {
      const ids = Array.isArray(store_ids) ? store_ids : [];
      if (ids.length === 0) {
        return false;
      }
      payloads = [];
      for (let i = 0; i < ids.length; i += BULK_STORE_ID_CHUNK) {
        payloads.push({
          ...basePayload,
          store_ids: ids.slice(i, i + BULK_STORE_ID_CHUNK),
        });
      }
    } else {
      payloads = [store_ids ? { ...basePayload, store_ids } : basePayload];
    }

    // The endpoint returns HTTP 200 even on partial failure and reports it in
    // the payload (failed_count, and company_updated when a default write was
    // requested). Treat those as failures so callers don't clear their pending
    // prompts / revert as if everything applied.
    const requestedCompanyWrite =
      scope === "company_only" || Boolean(update_company_default);

    try {
      toast.loading("Updating settings...", { autoClose: false });

      // Attempt every chunk (don't bail early) so we apply as much as possible,
      // then judge overall success from the payloads.
      let allSucceeded = true;
      for (const payload of payloads) {
        const res = await postRightworkAPI(
          API_bulk_update_feature_settings,
          payload
        );
        const body = res?.data?.redux?.payload || {};
        const chunkOk =
          res.status === 200 &&
          (body.failed_count || 0) === 0 &&
          (!requestedCompanyWrite || body.company_updated === true);
        if (!chunkOk) {
          allSucceeded = false;
        }
      }

      const pushedToStores = scope === "all" || scope === "stores";
      const wroteCompanyDefault = requestedCompanyWrite;

      // Refetch regardless of full success so any partial writes surface.
      const dataToRefresh = {};
      if (pushedToStores) {
        dataToRefresh.locationsData = API_get_manager_stores_rightwork_data;
      }
      if (wroteCompanyDefault) {
        dataToRefresh.companyFeatureSettings = API_get_company_feature_settings;
      }
      if (Object.keys(dataToRefresh).length > 0) {
        await fetch_data(dataToRefresh, api_params, false);
      }

      // Pushing flags to stores changes which nav tabs/features are gated on,
      // so propagate to the in-memory member stores immediately. A company
      // default write too: the company flags the gates read (company_info on
      // each member store) come from the same fetch.
      if (pushedToStores || wroteCompanyDefault) {
        await props.refreshMemberStores?.();
      }

      toast.dismiss();
      if (allSucceeded) {
        toast.success("Settings Updated!", { autoClose: 3000 });
        return true;
      }
      toast.error(
        "Some settings couldn't be updated. Please try again.",
        { autoClose: 4000 }
      );
      return false;
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);

      return false;
    }
  };

  // Push one company config surface ("clock" | "flagging" | "live_alerts") onto
  // the given locations. The company template itself was already saved by the
  // panel's auto-save; this only copies it outward.
  //
  // Mirrors bulk_update_features: chunk to the backend's MAX_BULK_STORE_IDS, try
  // every chunk, and judge success from the payload (the endpoint returns 200 on
  // partial failure). No feature flags change, so member stores don't need a
  // refresh; only the per-location configs moved.
  const bulk_apply_config_defaults = async ({ surface, store_ids }) => {
    const ids = Array.isArray(store_ids) ? store_ids : [];
    if (!selectedCompanyId || !surface || ids.length === 0) {
      return false;
    }

    const BULK_STORE_ID_CHUNK = 100;
    const payloads = [];
    for (let i = 0; i < ids.length; i += BULK_STORE_ID_CHUNK) {
      payloads.push({
        company_id: selectedCompanyId,
        surface,
        store_ids: ids.slice(i, i + BULK_STORE_ID_CHUNK),
      });
    }

    try {
      toast.loading("Applying settings...", { autoClose: false });

      let allSucceeded = true;
      for (const payload of payloads) {
        const res = await postRightworkAPI(
          API_bulk_apply_config_defaults,
          payload
        );
        const body = res?.data?.redux?.payload || {};
        if (res.status !== 200 || (body.failed_count || 0) > 0) {
          allSucceeded = false;
        }
      }

      toast.dismiss();
      if (allSucceeded) {
        toast.success("Settings Applied!", { autoClose: 3000 });
        return true;
      }
      toast.error("Some locations couldn't be updated. Please try again.", {
        autoClose: 4000,
      });
      return false;
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);

      return false;
    }
  };

  const link_or_create_store_from_pos = async (pos_store_uuid, store_id) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      store_id: store_id,
      pos_store_uuid: pos_store_uuid,
    };

    try {
      toast.loading(`${store_id ? "Linking Store" : "Creating Store"}`, { autoClose: false });

      const res = await postRightworkAPI(
        API_create_store_from_pos_store,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success(`${store_id ? "Store Linked!" : "Store Created!"}`, { autoClose: 3000 });

        const isCreatingStore = !store_id;
        const dataToRefresh = {
          locationsData: API_get_manager_stores_rightwork_data,
          posStores: API_list_pos_stores,
          integrationsData: API_get_manager_stores_integration_data,
          hoursOfOperation: API_get_hours_for_all_stores,
        };

        await fetch_data(dataToRefresh, api_params, false);

        if (isCreatingStore) {
          await props.refreshMemberStores?.();
        }
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const archive_pos_store = async (pos_store_uuid) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      pos_store_uuid: pos_store_uuid,
    };

    try {
      toast.loading("Excluding Store...", { autoClose: false });

      const res = await postRightworkAPI(
        API_archive_pos_store,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Store Excluded", { autoClose: 3000 });

        fetch_data(
          {
            posStores: API_list_pos_stores
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const update_integrations_info = async (store_id, integration_data) => {
    // Prepare data to send
    const requestData = {
      store_id: store_id,
      integration_data: JSON.stringify(integration_data),
    };

    try {
      toast.loading("Updating Integration Info...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_manager_store_integration_data,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Integration Info Updated!", { autoClose: 3000 });

        fetch_data(
          {
            integrationsData: API_get_manager_stores_integration_data,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };


  const storeNamesFiltered = props.storeNames?.filter(
    (s) => s.company === selectedCompanyId
  );

  const locationsDataFiltered = React.useMemo(() => {
    const allLocations = data?.locationsData?.redux?.payload?.stores || [];
    if (!selectedCompanyId) {
      return allLocations;
    }
    return allLocations.filter((loc) => loc.company === selectedCompanyId);
  }, [data?.locationsData, selectedCompanyId]);

  // The company's feature defaults — the template new locations inherit and
  // the source for the "Company Default" card at the top of Manage Locations.
  // Guard by the payload's company_id: the request is skipped for non-admin
  // companies, so stale defaults from a previously-selected admin company can
  // linger in `data` — only surface them when they match the current company.
  const companyFeatureSettingsPayload =
    data?.companyFeatureSettings?.redux?.payload;
  const companyFlags =
    companyFeatureSettingsPayload &&
    Number(companyFeatureSettingsPayload.company_id) === Number(selectedCompanyId)
      ? companyFeatureSettingsPayload.company_flags || null
      : null;

  // Regions and districts for the Store groups view and the district select
  // on the location drawer. Same company guard as the feature defaults.
  const storeGroupsPayload = data?.storeGroups?.redux?.payload || (data?.storeGroups?.error ? data.storeGroups : null);
  const storeGroups =
    storeGroupsPayload && Number(storeGroupsPayload.company_id) === Number(selectedCompanyId)
      ? storeGroupsPayload
      : null;
  const refresh_store_groups = async () => {
    await fetch_data({ locationsData: API_get_manager_stores_rightwork_data }, api_params, false);
    await fetch_store_groups();
    await props.refreshMemberStores?.();
  };

  const storeIdsForSelectedCompany = React.useMemo(() => {
    const idsFromStoreNames = storeNamesFiltered?.map((s) => s.id) || [];
    const idsFromLocations = locationsDataFiltered?.map((loc) => loc.id) || [];
    return Array.from(new Set([...idsFromStoreNames, ...idsFromLocations]));
  }, [storeNamesFiltered, locationsDataFiltered]);

  const integrationsDataFiltered = data?.integrationsData?.redux?.payload?.stores?.filter(
    (loc) => storeIdsForSelectedCompany.includes(loc.id)
  );

  const hoursOfOperationFiltered = data?.hoursOfOperation?.hours?.filter((loc) =>
    storeIdsForSelectedCompany.includes(loc.storeId)
  );

  const posStores = data?.posStores?.redux?.payload.pos_stores;

  const formatPosProviderName = React.useCallback((provider) => {
    if (!provider) {
      return null;
    }
    const providerNames = {
      toast: "Toast",
      heartland: "Heartland",
      square: "Square",
    };
    return providerNames[provider] || provider;
  }, []);

  const extractPosLocationName = React.useCallback((posDetails) => {
    if (!posDetails || typeof posDetails !== "object") {
      return null;
    }
    const candidateFields = [
      "name",
      "location_name",
      "locationName",
      "business_name",
      "businessName",
      "store_name",
      "storeName",
    ];
    for (const field of candidateFields) {
      if (posDetails[field]) {
        return posDetails[field];
      }
    }
    if (posDetails.venue?.name) {
      return posDetails.venue.name;
    }
    return null;
  }, []);

  const posLinkStatusMap = React.useMemo(() => {
    const map = {};
    (storeNamesFiltered || []).forEach((store) => {
      const posDetails =
        store?.pos_details_json && typeof store.pos_details_json === "object"
          ? store.pos_details_json
          : null;
      const provider = store?.pos_provider;
      map[store.id] = {
        hasPosLink: Boolean(posDetails) || Boolean(provider),
        provider,
        providerLabel: formatPosProviderName(provider),
        locationName: extractPosLocationName(posDetails),
      };
    });
    return map;
  }, [storeNamesFiltered, formatPosProviderName, extractPosLocationName]);

  const integrationsEditor = (
    <IntegrationsDisplay
      integrationsData={integrationsDataFiltered}
      update_integrations_info={update_integrations_info}
      company={selectedCompany}
      onIntegrationComplete={handle_pos_integration_complete}
      refresh_pos_locations={refresh_pos_locations}
      isRefreshingPosLocations={isRefreshingPosLocations}
      posLinkStatusMap={posLinkStatusMap}
      posStores={posStores}
      link_or_create_store_from_pos={link_or_create_store_from_pos}
      archive_pos_store={archive_pos_store}
      initialIntent={integrationIntent}
      onIntentHandled={() => setIntegrationIntent(null)}
      isAdminForSelectedCompany={isAdminForSelectedCompany}
      permissionRole={props.permissionRole}
    />
  );
  const renderLocationEditor = (editorId, location) => {
    if (editorId === "hours") {
      const hours = (hoursOfOperationFiltered || []).filter((row) => Number(row.storeId) === Number(location.id));
      return hours.length ? <HoursOfOperationDisplay embedded
        canCheckPublishedHours={isAdminForSelectedCompany}
        onHoursVerified={() => fetch_data({ hoursOfOperation: API_get_hours_for_all_stores }, api_params, false)}
        hoursOfOperation={hours} storeNames={storeNamesFiltered.filter((store) => Number(store.id) === Number(location.id))}
        update_hours_for_all_stores={update_hours_for_all_stores} isSavingData={isSavingData}
      /> : <p className="p-4 text-sm text-slate-500">Operating hours are not available for this location yet.</p>;
    }
    if (editorId === "labor-laws") return <LaborLawsDisplay company={selectedCompany}
      settingsStoreId={location.id} opsRefreshSignal={props.opsRefreshSignal} />;
    return null;
  };

  return (
    <>
      {!isLoading ? (
        <div className={props.settingsOverview ? "w-full flex" : "w-full min-h-screen flex"}>
          {!props.settingsView && <div className="w-1/5">
            <div className="h-full flex flex-col gap-2 px-6 py-5 bg-slate-50 border-r border-slate-200">
              <div className="mb-2">
                <CompanyNameSidebar
                  companyOptions={companyOptions}
                  selectedCompanyId={selectedCompanyId}
                  setSelectedCompanyId={props.onSettingsCompanyChange || setSelectedCompanyId}
                />
              </div>
              <button
                className={`w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
                  display === "Locations" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Locations")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Locations"
                      ? "text-violet-700"
                      : "text-slate-500"
                  }`}
                >
                  <div>
                    <LocationOnIcon />
                  </div>
                  <span>Manage Locations</span>
                </div>
              </button>
              <button
                className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                  display === "Hours" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Hours")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Hours" ? "text-violet-700" : "text-slate-500"
                  }`}
                >
                  <div>
                    <ScheduleIcon />
                  </div>
                  <span>Hours of Operations</span>
                </div>
              </button>
              <button
                className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                  display === "Integrations" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Integrations")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Integrations"
                      ? "text-violet-700"
                      : "text-slate-500"
                  }`}
                >
                  <div>
                    <CableIcon />
                  </div>
                  <span>Integrations</span>
                </div>
              </button>
              {(["company_admin", "rightwork_internal"].includes(props.permissionRole) || display === "Labor Laws") &&
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Labor Laws" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Labor Laws")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Labor Laws"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <GavelIcon />
                    </div>
                    <span>Labor Laws</span>
                  </div>
                </button>
              }
              {/* Time Clock, Timecard Flagging, and Live Action Alerts moved
                  into the Manage Locations cards (per-location config drawers). */}
            </div>
          </div>}
          <div className={props.settingsView ? "w-full" : "w-4/5"}>
            {!props.settingsView && !["Integrations", "Store Groups"].includes(display) && <div>
              <TopBarDisplayTitleNoLocationSelect title={display} />
            </div>}
            {(props.settingsOverview || display === "Locations") && (
              <div hidden={display !== "Locations"}>
              <ManageLocationsDisplay
                canEditContactDetails={isAdminForSelectedCompany}
                // Remount on company switch so per-company local state (pending
                // default applies, copied settings, open config drawer) can't
                // leak across companies and act on the wrong selectedCompanyId.
                key={selectedCompanyId}
                storeNames={props.storeNames}
                refreshMemberStores={props.refreshMemberStores}
                onFeatureSettingsDirtyChange={props.onFeatureSettingsDirtyChange}
                onOpenLaborModel={props.onOpenLaborModel}
                renderLocationEditor={renderLocationEditor}
                focusEditor={focusEditor}
                settingsActive={props.settingsActive !== false && display === "Locations"}
                companySettingsBucket={props.companySettingsBucket}
                companySettingsEditor={props.companySettingsEditor}
                opsRefreshSignal={props.opsRefreshSignal}
                locationsData={locationsDataFiltered}
                companyFlags={companyFlags}
                storeGroups={storeGroups?.groups}
                companyId={selectedCompanyId}
                update_store_info={update_store_info}
                bulk_update_features={bulk_update_features}
                bulk_apply_config_defaults={bulk_apply_config_defaults}
                posStores={posStores}
                link_or_create_store_from_pos={link_or_create_store_from_pos}
                archive_pos_store={archive_pos_store}
                permissionRole={props.permissionRole}
              />
              </div>
            )}
            {(display === "Integrations" || (props.settingsOverview && integrationsVisited)) && <div key={`integrations:${selectedCompanyId}`} hidden={display !== "Integrations"}>
              {integrationsEditor}
            </div>}
            {display === "Store Groups" ? (
              <StoreGroupsDisplay
                key={`store-groups:${selectedCompanyId}`}
                companyId={selectedCompanyId}
                payload={storeGroups}
                isAdmin={isAdminForSelectedCompany}
                onChanged={refresh_store_groups}
              />
            ) : display === "Hours" ? (
              <HoursOfOperationDisplay
                key={`hours:${selectedCompanyId}`}
                canCheckPublishedHours={isAdminForSelectedCompany}
                onHoursVerified={() => fetch_data({ hoursOfOperation: API_get_hours_for_all_stores }, api_params, false)}
                hoursOfOperation={hoursOfOperationFiltered}
                update_hours_for_all_stores={update_hours_for_all_stores}
                isSavingData={isSavingData}
                storeNames={storeNamesFiltered}
                focusStore={focusStore}
              />
            ) : display === "Labor Laws" ? (
              <LaborLawsDisplay
                company={selectedCompany}
                opsRefreshSignal={props.opsRefreshSignal}
                focusStore={focusStore}
              />
            ) : (
              <div></div>
            )}
          </div>
        </div>
      ) : (
        <SettingsPageLoading />
      )}
    </>
  );
}

export default LocationsIntegrationsHoursSettingsDisplay;
